import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { company } from '../data/company';
import { LANGUAGES, DEFAULT_LANGUAGE, getLanguage, localizeUrl, stripLangFromPath } from '../i18n/config';

type SeoProps = {
  title: string;
  description: string;
  path?: string;
  schema?: object;
  lang?: string;
};

export function Seo({ title, description, path = '/', schema, lang }: SeoProps) {
  // 优先使用传入的 lang，否则从 i18n 读取当前语言
  const { i18n } = useTranslation();
  const activeLang = lang || i18n.language || DEFAULT_LANGUAGE;
  const currentLanguage = getLanguage(activeLang);
  const url = localizeUrl(path, activeLang, company.domain);
  // 去掉语言前缀后的纯路径，用于生成各语言 hreflang 链接
  const cleanPath = stripLangFromPath(path);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* hreflang — 告知 Google 各语言版本关系 */}
      {LANGUAGES.map((language) => (
        <link
          key={language.code}
          rel="alternate"
          hrefLang={language.code}
          href={localizeUrl(cleanPath, language.code, company.domain)}
        />
      ))}
      {/* x-default 指向默认语言（英语） */}
      <link rel="alternate" hrefLang="x-default" href={localizeUrl(cleanPath, DEFAULT_LANGUAGE, company.domain)} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={currentLanguage.ogLocale} />

      {/* 各语言的 og:locale:alternate */}
      {LANGUAGES.filter((language) => language.code !== activeLang).map((language) => (
        <meta key={language.code} property="og:locale:alternate" content={language.ogLocale} />
      ))}

      {schema ? <script type="application/ld+json">{JSON.stringify(schema)}</script> : null}
    </Helmet>
  );
}
